import React, { useCallback, useState } from "react";

import {
  ActionButton,
  BottomActionSheet,
  CancelButton,
  HeaderInfo,
  HeaderSubtitle,
  HeaderTitle,
  SheetCard,
  SheetHeader,
} from "../../../../../components/BottomActionSheet";
import {
  DateFormat,
  formatCurrency,
  formatDate,
  formatSignedPercent,
} from "../../../../../utils";
import { MarketUpdateItemProps } from "./types";
import { KebabButton } from "../../../../../components/KebabButton";
import {
  CardRow,
  CardTopRow,
  CardDate,
  CardValue,
  CardMeta,
  CardMetaMuted,
} from "./styles";

export const MarketUpdateCard: React.FC<MarketUpdateItemProps> = ({
  item,
  actions,
}) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const hasChange = item.changeValue !== null;
  const isPositive = (item.changeValue ?? 0) >= 0;
  const valueText = formatCurrency(item.value);
  const dateText = formatDate(item.date, DateFormat.DMY_SPACE);
  const changeText = hasChange ? formatCurrency(item.changeValue) : "";
  const percentText =
    item.changePercent === null ? "—" : formatSignedPercent(item.changePercent);
  const metaText = [changeText, percentText].filter(Boolean).join(" · ");

  const visibleActions = actions.filter((action) => action.show);
  const hasActions = visibleActions.length > 0;

  const openSheet = useCallback(() => {
    setIsSheetOpen(true);
  }, []);

  const closeSheet = useCallback(() => {
    setIsSheetOpen(false);
  }, []);

  const runAction = useCallback(
    (action: () => void) => {
      closeSheet();
      action();
    },
    [closeSheet],
  );

  return (
    <CardRow>
      <CardTopRow>
        <CardDate>{dateText}</CardDate>
        {hasActions && (
          <KebabButton
            size="small"
            onClick={openSheet}
            aria-label="Market update options"
            aria-haspopup="dialog"
            data-testid={`market-update-actions-mobile-${item.id}`}
          />
        )}
      </CardTopRow>
      <CardValue>{valueText}</CardValue>
      {hasChange ? (
        <CardMeta $positive={isPositive}>{metaText}</CardMeta>
      ) : (
        <CardMetaMuted>First recorded value</CardMetaMuted>
      )}

      {hasActions && (
        <BottomActionSheet
          isOpen={isSheetOpen}
          onClose={closeSheet}
          ariaLabel="Market update actions"
        >
          <SheetCard>
            <SheetHeader>
              <HeaderInfo>
                <HeaderTitle>{valueText}</HeaderTitle>
                <HeaderSubtitle>{dateText}</HeaderSubtitle>
              </HeaderInfo>
            </SheetHeader>
            {visibleActions.map((action) => (
              <ActionButton
                key={action.id}
                type="button"
                $variant={
                  action.variant === "destructive" ? "destructive" : undefined
                }
                onClick={() => runAction(action.onClick)}
              >
                {action.label}
              </ActionButton>
            ))}
          </SheetCard>
          <SheetCard>
            <CancelButton type="button" onClick={closeSheet}>
              Cancel
            </CancelButton>
          </SheetCard>
        </BottomActionSheet>
      )}
    </CardRow>
  );
};
